(() => {
  if (typeof HTMLElement === 'undefined') return;

  const STOREFRONT_ENDPOINT = '/api/2026-04/graphql.json';
  const DOWNLOAD_PRODUCT_TYPE = 'download_product';

  const PRODUCT_CATALOG_QUERY = `
    query DownloadProductCatalog {
      metaobjects(type: "download_product", first: 250) {
        nodes {
          handle
          name: field(key: "name") { value }
          code: field(key: "code") { value }
          sortOrder: field(key: "sort_order") { value }
          enabled: field(key: "enabled") { value }
          manuals: field(key: "manuals") {
            references(first: 50) {
              nodes {
                ...DownloadFile
              }
            }
          }
          certificates: field(key: "certificates") {
            references(first: 50) {
              nodes {
                ...DownloadFile
              }
            }
          }
        }
      }
    }
    fragment DownloadFile on Metaobject {
      handle
      title: field(key: "title") { value }
      languageCode: field(key: "language_code") { value }
      languageLabel: field(key: "language_label") { value }
      fileUrl: field(key: "file_url") { value }
      sortOrder: field(key: "sort_order") { value }
      enabled: field(key: "enabled") { value }
    }
  `;

  const PRODUCT_QUERY = `
    query DownloadProduct($handle: String!) {
      metaobject(handle: { type: "download_product", handle: $handle }) {
        handle
        manuals: field(key: "manuals") {
          references(first: 50) {
            nodes {
              ...DownloadFile
            }
          }
        }
        certificates: field(key: "certificates") {
          references(first: 50) {
            nodes {
              ...DownloadFile
            }
          }
        }
      }
    }
    fragment DownloadFile on Metaobject {
      handle
      title: field(key: "title") { value }
      languageCode: field(key: "language_code") { value }
      languageLabel: field(key: "language_label") { value }
      fileUrl: field(key: "file_url") { value }
      sortOrder: field(key: "sort_order") { value }
      enabled: field(key: "enabled") { value }
    }
  `;

  const fieldValue = (item, key) => {
    if (!item) return '';
    const direct = item[key];
    if (typeof direct === 'string' || typeof direct === 'number' || typeof direct === 'boolean') return direct;
    if (direct && typeof direct.value !== 'undefined') return direct.value;
    const field = Array.isArray(item.fields) && item.fields.find((candidate) => candidate.key === key);
    return field ? field.value : '';
  };

  const nodesForField = (item, key) => {
    if (!item) return [];
    const direct = item[key];
    const field = direct || (Array.isArray(item.fields) && item.fields.find((candidate) => candidate.key === key));
    return field?.references?.nodes || [];
  };

  const normalizeDownloadFile = (item) => ({
    handle: item.handle,
    title: fieldValue(item, 'title'),
    languageCode: fieldValue(item, 'languageCode') || fieldValue(item, 'language_code'),
    languageLabel: fieldValue(item, 'languageLabel') || fieldValue(item, 'language_label'),
    fileUrl: fieldValue(item, 'fileUrl') || fieldValue(item, 'file_url'),
    sortOrder: fieldValue(item, 'sortOrder') || fieldValue(item, 'sort_order'),
    enabled: isEnabled(fieldValue(item, 'enabled')),
  });

  const isEnabled = (value) => value === true || String(value).toLowerCase() === 'true' || String(value) === '1';
  const numericSort = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : Number.MAX_SAFE_INTEGER;
  };
  const compareBySortThenHandle = (left, right) => numericSort(left.sortOrder) - numericSort(right.sortOrder)
    || left.handle.localeCompare(right.handle);

  const isValidDownloadUrl = (value) => {
    if (typeof value !== 'string') return false;
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && url.port === ''
        && /^\/s\/files\/[^/]+/.test(url.pathname);
    } catch (_) {
      return false;
    }
  };

  class RheidonDownloadCenter extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.productRequestId = 0;
      this.productsPromise = null;
      this.productCache = new Map();
      this.state = {
        mode: this.dataset.defaultMode === 'certificate' ? 'certificate' : 'manual',
        products: [],
        selectedProduct: null,
        documents: [],
        selectedDocument: null,
      };
      this.elements = {
        modeButtons: Array.from(this.querySelectorAll('[data-download-mode]')),
        product: this.querySelector('[data-download-product]'),
        documents: this.querySelector('[data-download-documents]'),
        document: this.querySelector('[data-download-document]'),
        documentLabel: this.querySelector('[data-download-document-label]'),
        download: this.querySelector('[data-download-action]'),
        status: this.querySelector('[data-download-status]'),
        fallback: this.querySelector('[data-download-fallback]'),
      };

      if (!this.dataset.storefrontToken?.trim()) {
        this.setStatus('');
        this.showFallback();
        this.updateDownloadAction();
        return;
      }

      this.elements.modeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const mode = button.dataset.downloadMode === 'certificates' ? 'certificate' : 'manual';
          this.setMode(mode);
        });
      });
      this.elements.product?.addEventListener('change', () => this.selectProduct(this.elements.product.value));
      this.elements.document?.addEventListener('change', () => this.selectDocument(this.elements.document.value));
      this.elements.documents?.addEventListener('click', (event) => {
        const button = event.target.closest?.('[data-download-document-handle]');
        if (button) this.selectDocument(button.dataset.downloadDocumentHandle);
      });
      this.elements.download?.addEventListener('click', (event) => {
        if (!this.state.selectedDocument || !isValidDownloadUrl(this.state.selectedDocument.fileUrl)) {
          event.preventDefault();
          return;
        }
        this.openSelectedDocument();
      });

      this.updateDownloadAction();
      this.hideFallback();
      this.loadProducts()
        .then(() => {
          this.renderProducts();
          this.setStatus('');
        })
        .catch(() => this.setStatus('Downloads are unavailable right now. Please try again later.'));
    }

    async fetchGraphql(query, variables = {}) {
      const response = await fetch(STOREFRONT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.dataset.storefrontToken || '',
        },
        body: JSON.stringify({ query, variables }),
      });
      if (!response.ok) throw new Error(`Storefront request failed (${response.status})`);
      const payload = await response.json();
      if (payload.errors?.length) throw new Error(payload.errors.map((error) => error.message).join('; '));
      return payload;
    }

    loadProducts() {
      if (this.productsPromise) return this.productsPromise;
      this.productsPromise = this.fetchGraphql(PRODUCT_CATALOG_QUERY)
        .then((payload) => {
          const products = [];
          this.productCache ??= new Map();
          (payload.data?.metaobjects?.nodes || []).forEach((item) => {
            if (!item.handle || !isEnabled(fieldValue(item, 'enabled'))) return;
            const manuals = nodesForField(item, 'manuals')
              .map(normalizeDownloadFile)
              .filter((file) => file.handle && file.enabled)
              .sort(compareBySortThenHandle);
            const certificates = nodesForField(item, 'certificates')
              .map(normalizeDownloadFile)
              .filter((file) => file.handle && file.enabled)
              .sort(compareBySortThenHandle);
            this.productCache.set(item.handle, {
              handle: item.handle,
              manuals,
              certificates,
            });
            products.push({
              handle: item.handle,
              name: fieldValue(item, 'name'),
              code: fieldValue(item, 'code'),
              sortOrder: fieldValue(item, 'sortOrder') || fieldValue(item, 'sort_order'),
              enabled: true,
              hasCertificates: certificates.length > 0,
            });
          });
          products.sort(compareBySortThenHandle);
          this.state.products = products;
          return products;
        })
        .catch((error) => {
          this.productsPromise = null;
          throw error;
        });
      return this.productsPromise;
    }

    productsForMode() {
      const products = this.state.products || [];
      if (this.state.mode !== 'certificate') return products;
      return products.filter((product) => product.hasCertificates);
    }

    async loadProduct(handle) {
      if (this.productCache?.has(handle)) return this.productCache.get(handle);
      const payload = await this.fetchGraphql(PRODUCT_QUERY, { handle });
      const metaobject = payload.data?.metaobject;
      if (!metaobject) throw new Error('Requested download product was not found');
      const product = {
        handle: metaobject.handle,
        manuals: nodesForField(metaobject, 'manuals').map(normalizeDownloadFile).filter((file) => file.handle && file.enabled).sort(compareBySortThenHandle),
        certificates: nodesForField(metaobject, 'certificates').map(normalizeDownloadFile).filter((file) => file.handle && file.enabled).sort(compareBySortThenHandle),
      };
      this.productCache ??= new Map();
      this.productCache.set(handle, product);
      return product;
    }

    setMode(mode) {
      if (mode !== 'manual' && mode !== 'certificate') return;
      this.state.mode = mode;
      const products = this.productsForMode();
      if (this.state.selectedProduct && !products.some((product) => product.handle === this.state.selectedProduct.handle)) {
        this.state.selectedProduct = null;
        if (this.elements?.product) this.elements.product.value = '';
      }
      this.resetDependentSelection();
      this.renderProducts();
      this.state.documents = this.documentsForSelectedProduct();
      this.renderDocuments();
      if (this.elements?.documentLabel) {
        this.elements.documentLabel.textContent = mode === 'certificate'
          ? (this.dataset.certificateCopy || 'Choose a certificate')
          : (this.dataset.languageCopy || 'Choose your language');
      }
      this.elements?.modeButtons?.forEach((button) => {
        const buttonMode = button.dataset.downloadMode === 'certificates' ? 'certificate' : 'manual';
        const active = buttonMode === mode;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });
    }

    async selectProduct(handle) {
      const requestId = ++this.productRequestId;
      this.state.selectedProduct = null;
      this.resetDependentSelection();
      this.renderDocuments();
      if (!handle) {
        this.setStatus('');
        return;
      }
      this.setStatus('Loading downloads…');
      try {
        const product = await this.loadProduct(handle);
        if (requestId !== this.productRequestId) return;
        this.state.selectedProduct = product;
        this.state.documents = this.documentsForSelectedProduct();
        this.renderDocuments();
        this.setStatus(this.state.documents.length ? '' : 'No downloads are available for this selection.');
      } catch (_) {
        if (requestId !== this.productRequestId) return;
        this.setStatus('Downloads are unavailable right now. Please try again later.');
      }
    }

    selectDocument(handle) {
      this.state.selectedDocument = this.state.documents.find((document) => document.handle === handle) || null;
      this.elements?.document && (this.elements.document.value = this.state.selectedDocument?.handle || '');
      this.renderDocuments();
      this.updateDownloadAction();
    }

    renderProducts() {
      const select = this.elements?.product;
      if (!select || typeof document === 'undefined') return;
      const placeholder = select.dataset.placeholder || 'Select a product';
      select.replaceChildren();
      const empty = document.createElement('option');
      empty.value = '';
      empty.textContent = placeholder;
      select.append(empty);
      const products = this.productsForMode();
      products.forEach((product) => {
        const option = document.createElement('option');
        option.value = product.handle;
        const displayName = (product.name || '').replace(/\s*User Manual\s*$/i, '') || product.code || product.handle;
        option.textContent = displayName;
        select.append(option);
      });
      select.value = this.state.selectedProduct?.handle || '';
      select.disabled = products.length === 0;
      select.setAttribute?.('aria-disabled', String(select.disabled));
    }

    renderDocuments() {
      const documents = this.elements?.documents;
      if (documents && typeof document !== 'undefined') {
        documents.replaceChildren();
        this.state.documents.forEach((item) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.downloadDocumentHandle = item.handle;
          button.textContent = item.label;
          button.setAttribute('aria-pressed', String(this.state.selectedDocument?.handle === item.handle));
          documents.append(button);
        });
      }
      const select = this.elements?.document;
      if (select && typeof document !== 'undefined') {
        select.replaceChildren();
        this.state.documents.forEach((item) => {
          const option = document.createElement('option');
          option.value = item.handle;
          option.textContent = item.label;
          select.append(option);
        });
        select.value = this.state.selectedDocument?.handle || '';
      }
      this.updateDownloadAction();
    }

    resetDependentSelection() {
      this.state.documents = [];
      this.state.selectedDocument = null;
      if (this.elements?.document) this.elements.document.value = '';
      this.updateDownloadAction();
    }

    setStatus(message, state = 'idle') {
      if (this.elements?.status) {
        this.elements.status.textContent = message;
        this.elements.status.setAttribute('data-state', state);
      }
    }

    showFallback() {
      if (this.elements?.fallback) this.elements.fallback.hidden = false;
    }

    hideFallback() {
      if (this.elements?.fallback) this.elements.fallback.hidden = true;
    }

    updateDownloadAction() {
      const documentControl = this.elements?.document;
      const documentsAvailable = this.state?.documents?.some((document) => isValidDownloadUrl(document.fileUrl));
      if (documentControl) {
        documentControl.disabled = !documentsAvailable;
        documentControl.setAttribute?.('aria-disabled', String(!documentsAvailable));
      }
      const action = this.elements?.download;
      if (!action) return;
      const fileUrl = this.state?.selectedDocument?.fileUrl;
      const available = isValidDownloadUrl(fileUrl);
      action.disabled = !available;
      action.setAttribute?.('aria-disabled', String(!available));
      action.classList?.toggle('is-disabled', !available);
      if (action.tagName === 'A') {
        if (available) action.setAttribute('href', fileUrl);
        else action.removeAttribute?.('href');
      }
    }

    openSelectedDocument() {
      const fileUrl = this.state?.selectedDocument?.fileUrl;
      if (!isValidDownloadUrl(fileUrl) || typeof window === 'undefined' || typeof window.open !== 'function') return false;
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
      return true;
    }

    documentsForSelectedProduct() {
      const product = this.state.selectedProduct;
      if (!product) return [];
      const files = this.state.mode === 'certificate' ? product.certificates : product.manuals;
      return (files || [])
        .map((file) => ({
          handle: file.handle,
          title: file.title || fieldValue(file, 'title'),
          languageLabel: file.languageLabel || fieldValue(file, 'languageLabel') || fieldValue(file, 'language_label'),
          fileUrl: file.fileUrl || fieldValue(file, 'fileUrl') || fieldValue(file, 'file_url'),
          sortOrder: file.sortOrder || fieldValue(file, 'sortOrder') || fieldValue(file, 'sort_order'),
          enabled: typeof file.enabled === 'boolean'
            ? file.enabled
            : (fieldValue(file, 'enabled') === '' || isEnabled(fieldValue(file, 'enabled'))),
        }))
        .filter((file) => file.enabled)
        .sort(compareBySortThenHandle)
        .map((file) => ({
          handle: file.handle,
          label: this.state.mode === 'certificate' ? file.title : file.languageLabel,
          fileUrl: file.fileUrl,
        }))
        .filter((file) => file.handle && file.label);
    }
  }

  if (typeof customElements !== 'undefined' && typeof customElements.get === 'function'
    && !customElements.get('rheidon-download-center')) {
    customElements.define('rheidon-download-center', RheidonDownloadCenter);
  }
})();
